import classNames from 'classnames'

import { Icon } from '@/Atomic/Atoms/Icon'

import './TimelineItem.scss'

export interface TimelineItemProps {
  /**
   * Icon of the entry type shown in the timeline rail, e.g. `coffee` for an
   * appointment. Decorative, because the type is already named in the `header`.
   */
  icon: string
  /** Pre-header of the entry: what changed, by whom and when. */
  header: React.ReactNode
  /**
   * Where the entry comes from, rendered below the card. Used by follow-up
   * activities to link the activity they were created for.
   */
  origin?: React.ReactNode
  className?: string
  /**
   * Card of the entry, e.g. an activity or an e-mail card. Entries without a
   * card render the header only.
   */
  children?: React.ReactNode
}

/**
 * Layout of a single entry of the activity timeline.
 *
 * Places the timeline rail — the type icon plus the connector line that joins
 * the entry to the following one — next to a content column that stacks the
 * pre-header, the card and the origin. Purely presentational: it neither reads
 * data nor renders the card itself.
 *
 * Meant to be used inside a `TimelineSection`, whose chevron reserves the same
 * rail width so that group headers and entries share one vertical axis.
 */
export const TimelineItem: React.FC<TimelineItemProps> = ({
  icon,
  header,
  origin,
  className,
  children,
}) => (
  <div className={classNames('timeline-item', className)}>
    <div className="timeline-item__rail" aria-hidden="true">
      <span className="timeline-item__icon">
        <Icon icon={icon} size="md" color="strong" />
      </span>
      <span className="timeline-item__connector" />
    </div>
    <div className="timeline-item__content">
      <div className="timeline-item__header">{header}</div>
      {children}
      {origin ? <div className="timeline-item__origin">{origin}</div> : null}
    </div>
  </div>
)
